import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";

/** Generic admin CRUD list used for teams, campaigns and partners. */
export const CrudPage = ({ titleKey, newKey, endpoint, fields, testPrefix, subtitle }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [extra, setExtra] = useState({});
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get(`/${endpoint}`).then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    const needsLeaders = fields.some((f) => f.options === "leaders");
    if (needsLeaders) {
      api.get("/users", { params: { role: "team_leader" } })
        .then(({ data }) => setExtra((x) => ({ ...x, leaders: data })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "number") payload[f.name] = Number(payload[f.name]) || 0; });
    try {
      if (editId) await api.patch(`/${endpoint}/${editId}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(t("common.saved"));
      setOpen(false); setForm({}); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/${endpoint}/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid={`${testPrefix}-page`}>
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-extrabold">{t(titleKey)}</h1>
        <Button data-testid={`${testPrefix}-new-btn`} className="h-12 rounded-full font-bold"
          onClick={() => { setForm({}); setEditId(null); setOpen(true); }}>
          <Plus className="mr-1 h-4 w-4" />{t(newKey)}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((it) => (
          <div key={it.id} className="vy-card p-5" data-testid={`${testPrefix}-card-${it.id}`}>
            <p className="font-display text-lg font-bold">{it.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">{subtitle ? subtitle(it, t) : ""}</p>
            <div className="mt-4 flex gap-2">
              <Button data-testid={`${testPrefix}-edit-btn-${it.id}`} variant="outline"
                className="h-11 rounded-full font-semibold"
                onClick={() => { setEditId(it.id); setForm(it); setOpen(true); }}>{t("common.edit")}</Button>
              <Button data-testid={`${testPrefix}-delete-btn-${it.id}`} variant="ghost"
                className="h-11 rounded-full text-destructive" onClick={() => remove(it.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="vy-card p-6 text-sm text-muted-foreground">{t("common.none")}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testPrefix}-dialog`} className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? t("common.edit") : t(newKey)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.name}>
                <label className="text-xs font-bold">{t(f.labelKey)}</label>
                {f.type === "textarea" ? (
                  <Textarea data-testid={`${testPrefix}-${f.name}-input`} rows={3} value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
                ) : f.options === "leaders" ? (
                  <select data-testid={`${testPrefix}-${f.name}-select`}
                    className="h-12 w-full rounded-xl border border-input px-3"
                    value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value || null })}>
                    <option value="">{t("common.none")}</option>
                    {(extra.leaders || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                ) : (
                  <Input data-testid={`${testPrefix}-${f.name}-input`} className="h-12" type={f.type || "text"}
                    value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid={`${testPrefix}-save-btn`} onClick={save} className="h-12 rounded-full font-bold">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const AdminTeams = () => (
  <CrudPage titleKey="nav.teams" newKey="admin.newTeam" endpoint="teams" testPrefix="teams"
    subtitle={(it, t) => `${it.region || ""} · ${it.leader_name || t("common.none")} · ${it.member_count || 0}`}
    fields={[
      { name: "name", labelKey: "admin.name" },
      { name: "region", labelKey: "events.location" },
      { name: "leader_id", labelKey: "admin.team_leader", options: "leaders" },
    ]} />
);

export const AdminCampaigns = () => (
  <CrudPage titleKey="nav.campaigns" newKey="admin.newCampaign" endpoint="campaigns" testPrefix="campaigns"
    subtitle={(it, t) => `${it.objective || ""} · ${it.event_count || 0} ${t("nav.events").toLowerCase()}`}
    fields={[
      { name: "name", labelKey: "admin.name" },
      { name: "objective", labelKey: "events.objectives" },
      { name: "description", labelKey: "events.agenda", type: "textarea" },
    ]} />
);

export const AdminPartners = () => (
  <CrudPage titleKey="nav.partners" newKey="admin.newPartner" endpoint="partners" testPrefix="partners"
    subtitle={(it) => [it.category, it.contact_name, it.address].filter(Boolean).join(" · ")}
    fields={[
      { name: "name", labelKey: "admin.name" },
      { name: "category", labelKey: "events.campaign" },
      { name: "contact_name", labelKey: "admin.name" },
      { name: "contact_email", labelKey: "auth.email" },
      { name: "address", labelKey: "events.location" },
      { name: "notes", labelKey: "events.agenda", type: "textarea" },
    ]} />
);
